import { Trophy, Clock, CheckCircle2 } from 'lucide-react';

/**
 * FeaturedRewardCard — compact hero card shown on Home + Profiles.
 *
 * `size='full'`  → full Home banner with progress bar + deadline pill
 * `size='mini'`  → tight badge for the Profiles child-select tiles
 *
 * Renders nothing when there's no featured reward (parent hasn't set one,
 * or all rewards are expired/completed).
 */
export function FeaturedRewardCard({ reward, totalPoints = 0, size = 'full' }) {
  if (!reward) return null;
  const target = Math.max(1, reward.target_points || 1);
  const progress = Math.max(0, Math.min(target, (reward.progress ?? (totalPoints - (reward.starting_points || 0)))));
  const pct = Math.round((progress / target) * 100);
  const completed = reward.status === 'completed' || pct >= 100;
  const daysLeft = daysUntil(reward.deadline_at);

  if (size === 'mini') {
    return (
      <div data-testid={`mini-reward-${reward.reward_id}`}
        className="mt-2 bg-white/95 rounded-2xl px-3 py-2 flex items-center gap-2 shadow-sm border border-slate-200 max-w-[9rem]">
        <Trophy size={14} strokeWidth={2.6} className="text-amber-500 flex-shrink-0"/>
        <div className="min-w-0">
          <div className="text-[10px] font-bold text-slate-500 uppercase truncate">{completed ? 'Unlocked' : 'Chasing'}</div>
          <div className="text-xs font-extrabold truncate">{reward.title}</div>
          <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${completed ? 'bg-emerald-500' : 'bg-[#FF6B1A]'}`} style={{ width: `${pct}%` }}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="home-reward-banner"
      className={`rounded-3xl p-5 mb-6 flex items-center gap-4 border-2 ${completed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#F4B740]/60'}`}>
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${completed ? 'bg-emerald-500' : 'bg-[#F4B740]'}`}>
        {completed
          ? <CheckCircle2 size={28} strokeWidth={2.5} className="text-white"/>
          : <Trophy size={28} strokeWidth={2.5} className="text-[#2B2140]"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
          {completed ? 'Reward unlocked!' : 'Reward in progress'}
        </div>
        <div data-testid="home-reward-title" className="text-lg font-extrabold truncate">{reward.title}</div>
        <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div data-testid="home-reward-progress-bar"
            className={`h-full rounded-full ${completed ? 'bg-emerald-500' : 'bg-[#FF6B1A]'}`}
            style={{ width: `${pct}%` }}/>
        </div>
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          <span data-testid="home-reward-progress-text" className="text-xs font-bold text-slate-600">
            {progress} / {target} pts · {pct}%
          </span>
          {reward.deadline_at && !completed && (
            <span data-testid="home-reward-deadline"
              className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${daysLeft < 0 ? 'bg-red-50 text-red-700' : daysLeft <= 3 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
              <Clock size={11} strokeWidth={3}/>
              {daysLeft < 0 ? 'Deadline passed' : daysLeft === 0 ? 'Ends today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const target = new Date(iso.slice(0, 10) + 'T23:59:59');
    const now = new Date();
    return Math.ceil((target - now) / 86400000);
  } catch { return null; }
}
